import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Eye, UserPlus, Power, PowerOff, Check, X as XIcon, ShieldCheck, Pencil, AlertCircle, Loader2, Search, Link2, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  Section,
  Field,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";
import { UserSearchOrCreate } from "@/components/UserSearchOrCreate";
import { CambiarEstatusModal } from "@/components/CambiarEstatusModal";
import { UploadDocumentoModal } from "@/components/UploadDocumentoModal";
import { DocumentoPreviewModal } from "@/components/DocumentoPreviewModal";
import { RevisionCalidadSection, type ObservacionQa } from "@/components/RevisionCalidadSection";
import {
  RepresentanteFormFields,
  normalizarRepresentante,
  validarRepresentante,
} from "@/components/RepresentanteFormFields";
import type { RepresentanteBorrador } from "@/pages/mi-registro-legal/types";
import { useEsAdminReal, useSuplantacion, useTieneRol } from "@/lib/sesion";
import {
  RelacionesTramitacionPanel,
  useRelacionesTramitacion,
} from "@/pages/detalle/RelacionesTramitacion";

interface Domicilio {
  calle: string;
  numeroExterior: string;
  numeroInterior: string | null;
  colonia: string;
  municipio: string;
  entidadFederativa: string;
  codigoPostal: string;
  pais: string;
}
interface Sector {
  id: string;
  codigo: string;
  nombre: string;
}
interface CartaResumen {
  id: string;
  titularRazonSocial: string;
  tramitadorRazonSocial: string | null;
  estatus: string;
  documentoId: string | null;
  vigenciaFin: string | null;
  sinVencimiento: boolean;
}
interface RepResumen {
  id: string;
  nombreCompleto: string;
  correo: string | null;
  estatus: string;
  usuarioId: string | null;
  instrumentoPoderDocumentoId: string | null;
  tipoRepresentacion: string | null;
  instrumentoNumero: string | null;
}
/** Usuario autorizado para trámites (asociación legal interna, F1 2026-07). */
interface UsuarioAutorizadoItem {
  asociacionId: string;
  usuarioId: string;
  nombreCompleto: string;
  correo: string | null;
  puestoArea: string | null;
  tipoAutorizacion: string;
  representanteLegalOtorganteId: string | null;
  otorganteNombre: string | null;
  sectoresCodigos: string[];
  vigenciaInicio: string;
  vigenciaFin: string | null;
  estatus: string;
  motivoCambio: string | null;
  cartaPoderId: string;
  cartaDocumentoId: string | null;
  fechaCreacion: string;
}

const TIPO_REPRESENTACION_LABEL: Record<string, string> = {
  REPRESENTANTE_LEGAL: "Representante legal",
  APODERADO_GENERAL: "Apoderado general",
  APODERADO_ESPECIAL: "Apoderado especial",
  OTRO: "Otro",
};
const TIPO_AUTORIZACION_LABEL: Record<string, string> = {
  INTERNO_TITULAR: "Interno del titular",
  CONSULTA: "Consulta",
  GESTION: "Gestión",
  SEGUIMIENTO: "Seguimiento",
  FIRMA: "Firma",
};
/** «Inactiva» del requerimiento = SUSPENDIDA (reversible); REVOCADA es terminal. */
const ESTATUS_AUTORIZACION_LABEL: Record<string, string> = {
  ACTIVA: "Activa",
  SUSPENDIDA: "Inactiva",
  REVOCADA: "Revocada",
  VENCIDA: "Vencida",
  SUSTITUIDA: "Sustituida",
  PENDIENTE: "Pendiente",
};
interface DocumentoLink {
  id: string;
  tipoCodigo: string;
  nombre: string;
  estatus: string;
}
interface RequisitoEstado {
  tipoDocumentoId: string;
  tipoDocumentoCodigo: string;
  tipoDocumentoNombre: string;
  obligatorio: boolean;
  exigeCopiaCertificada: boolean;
  presente: boolean;
  cumpleCopiaCertificada: boolean;
  documentoLegalId: string | null;
  observacion: string | null;
  requiereRevision: boolean;
  motivoRevision: string | null;
}
interface CompletitudDocumental {
  registroLegalId: string;
  tipoRegistro: string;
  tipoPersona: string | null;
  totalRequeridos: number;
  totalPresentes: number;
  totalFaltantes: number;
  completo: boolean;
  requisitos: RequisitoEstado[];
}
interface RegistroLegalDetalle {
  id: string;
  folioRegistro: string | null;
  tipoPersona: string;
  razonSocial: string;
  nombreComercial: string | null;
  rfc: string;
  curp: string | null;
  correoPrincipal: string;
  telefono: string;
  paginaElectronica: string | null;
  giroEmpresa: string | null;
  domicilio: Domicilio;
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  tieneFiliales: boolean;
  estatus: string;
  fechaCreacion: string;
  fechaActualizacion: string;
  sectores: Sector[];
  cartasComoTitular: CartaResumen[];
  cartasComoTramitador: CartaResumen[];
  representantes: RepResumen[];
  documentos: DocumentoLink[];
  // Campos del ciclo de captura/revisión (Fase RL-1b/c).
  pasoActual: number;
  fechaEnvioRevision: string | null;
  solicitanteUsuarioId: string | null;
  revisadoPor: string | null;
  motivoRechazo: string | null;
  observacionesQa: ObservacionQa[] | null;
  // Motivo y actor del retiro de operación (BAJA / CANCELADO).
  motivoCambio: string | null;
  actualizadoPor: string | null;
  actualizadoPorCorreo: string | null;
}

/** Lo que se llevaría por delante una BAJA o CANCELACIÓN de este registro. */
interface ImpactoCambioEstatus {
  representantesVigentes: number;
  asociacionesVigentes: number;
  usuariosAfectados: number;
  usuariosSinOtraRelacion: {
    usuarioId: string;
    nombreCompleto: string;
    correo: string;
    protegidoPorRol: boolean;
  }[];
}

const ESTATUS_TERMINALES = new Set(["BAJA", "CANCELADO"]);

const ROLES_CALIDAD = new Set(["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]);

/**
 * Editor de sectores habilitados (solo Calidad/Admin en esta pantalla): todos
 * los sectores del catálogo como chips-toggle. Usa el endpoint dedicado
 * PUT /registros-legales/{id}/sectores; el cliente puede autogestionarlos
 * también desde «Mi Registro Legal» (ajuste 2026-07-12).
 */
function SectoresHabilitadosEditor({ rlId, habilitados }: { rlId: string; habilitados: Sector[] }) {
  const qc = useQueryClient();
  const [pendiente, setPendiente] = useState<string | null>(null);
  const [errorSector, setErrorSector] = useState<string | null>(null);
  const qSectores = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => (await api.get<Sector[]>("/api/catalogos/sectores")).data,
    staleTime: 300_000,
  });
  const codigosActivos = new Set(habilitados.map((s) => s.codigo));

  const toggle = async (codigo: string) => {
    setPendiente(codigo);
    setErrorSector(null);
    const codigos = new Set(codigosActivos);
    if (codigos.has(codigo)) codigos.delete(codigo);
    else codigos.add(codigo);
    try {
      await api.put(`/api/registros-legales/${rlId}/sectores`, { sectoresCodigos: [...codigos] });
      await qc.invalidateQueries({ queryKey: ["registro-legal", rlId] });
      await qc.invalidateQueries({ queryKey: ["contextos-mios"] });
    } catch (e: any) {
      setErrorSector(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo actualizar el sector.",
      );
    } finally {
      setPendiente(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {(qSectores.data ?? []).map((s) => {
          const activo = codigosActivos.has(s.codigo);
          return (
            <button
              key={s.id}
              type="button"
              title={activo ? `Deshabilitar ${s.nombre}` : `Habilitar ${s.nombre}`}
              onClick={() => toggle(s.codigo)}
              disabled={pendiente !== null}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition disabled:opacity-60 ${
                activo
                  ? "border-estado-ok/40 bg-estado-ok-bg text-estado-ok"
                  : "border-edge-strong bg-surface text-fg-subtle hover:bg-surface-2"
              }`}
            >
              {pendiente === s.codigo
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <span className={`h-2 w-2 rounded-full ${activo ? "bg-estado-ok" : "bg-edge-strong"}`} />}
              {s.nombre}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-fg-subtle">
        El cambio surte efecto de inmediato en las validaciones y catálogos. No afecta
        solicitudes ya creadas.
      </p>
      {errorSector && <p className="mt-1 text-xs font-medium text-estado-error">{errorSector}</p>}
    </div>
  );
}

/**
 * Advertencia de impacto antes de confirmar una BAJA o CANCELACIÓN (H1).
 *
 * Nombra a las personas que perderían su última relación con una razón social,
 * porque son las únicas cuya cuenta SICEN está en juego. Las protegidas por rol
 * administrativo se listan igual: conservan el acceso, pero se están quedando
 * sin relación vigente y quien opera debe saberlo.
 */
function AvisoImpactoTerminal({
  nuevoEstatus,
  impacto,
  cargando,
}: {
  nuevoEstatus: string;
  impacto: ImpactoCambioEstatus | undefined;
  cargando: boolean;
}) {
  const accion = nuevoEstatus === "CANCELADO" ? "cancelar" : "dar de baja";

  if (cargando) {
    return (
      <div className="flex items-center gap-2 rounded border bg-surface-2 p-2.5 text-xs text-fg-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Calculando el impacto de esta operación…
      </div>
    );
  }
  if (!impacto) return null;

  const enRiesgo = impacto.usuariosSinOtraRelacion;
  const sinRelaciones =
    impacto.representantesVigentes === 0 && impacto.asociacionesVigentes === 0;

  return (
    <div className="space-y-2">
      <div className="rounded border border-estado-info/25 bg-estado-info-bg p-2.5 text-xs text-estado-info">
        {sinRelaciones ? (
          <>Este registro no tiene representantes ni autorizaciones vigentes: al {accion} no se revocará nada.</>
        ) : (
          <>
            Al {accion} este registro se revocarán{" "}
            <strong>{impacto.representantesVigentes} representante(s)</strong> y{" "}
            <strong>{impacto.asociacionesVigentes} autorización(es)</strong>, que alcanzan a{" "}
            <strong>{impacto.usuariosAfectados} persona(s)</strong>.
          </>
        )}
      </div>

      {enRiesgo.length > 0 && (
        <div className="rounded border border-estado-warn/40 bg-estado-warn-bg p-2.5 text-xs text-estado-warn">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1.5">
              <p className="font-semibold">
                Atención: {enRiesgo.length} persona(s) quedarán sin ninguna relación activa.
              </p>
              <ul className="space-y-1">
                {enRiesgo.map((u) => (
                  <li key={u.usuarioId}>
                    <span className="font-medium">{u.correo || u.nombreCompleto}</span>
                    {u.protegidoPorRol ? (
                      <span className="block text-[11px] opacity-90">
                        La cuenta conserva acceso por su rol administrativo, pero esta es su última
                        relación vigente con una razón social.
                      </span>
                    ) : (
                      <span className="block text-[11px] opacity-90">
                        Su cuenta SICEN será deshabilitada.
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p>Verifique esta información antes de continuar.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Confirmación antes de entrar a la sesión de otra persona. Es una operación
 * sensible y queda registrada, así que conviene decir antes qué implica y pedir
 * el motivo —opcional, pero es lo que después explica el evento en la bitácora—.
 */
function SuplantarUsuarioModal({
  objetivo,
  onClose,
  onIniciada,
}: {
  objetivo: { usuarioId: string; nombre: string };
  onClose: () => void;
  onIniciada: () => Promise<void> | void;
}) {
  const [motivo, setMotivo] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post("/api/suplantacion/iniciar", {
        usuarioId: objetivo.usuarioId,
        motivo: motivo.trim() || null,
      });
    },
    onSuccess: async () => {
      await onIniciada();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">Suplantar a {objetivo.nombre}</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded p-1 transition hover:bg-surface-2"
            aria-label="Cerrar"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <p className="rounded-lg border border-estado-warn/40 bg-estado-warn-bg p-3 text-xs text-estado-warn">
            Vas a navegar SICEN <strong>exactamente como esta persona</strong>: sus roles, sus razones
            sociales, sus sectores, sus bandejas y sus restricciones. Lo que hagas se ejecuta con su
            identidad, y la bitácora deja registrado que fuiste tú quien estaba detrás.
          </p>
          <p className="text-xs text-fg-subtle">
            No necesitas —ni verás— su contraseña. Podrás volver a tu sesión con «Salir de
            suplantación», que estará visible en todo momento.
          </p>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Motivo</span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              placeholder="p. ej. verificar que puede crear solicitudes de Telecom"
            />
            <span className="mt-1 block text-xs text-fg-subtle">
              Opcional. Queda en la bitácora junto al inicio de la suplantación.
            </span>
          </label>

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t bg-surface px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="cursor-pointer rounded bg-estado-warn px-4 py-1.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mut.isPending ? "Iniciando…" : "Navegar como esta persona"}
          </button>
        </div>
      </div>
    </div>
  );
}

function tonePorEstatus(estatus: string): "ok" | "info" | "warn" | "neutral" {
  if (estatus.startsWith("APROBADO") || estatus === "ACTIVO") return "ok";
  if (estatus.startsWith("RECHAZADO") || estatus === "REVOCADA" || estatus === "VENCIDA") return "warn";
  return "info";
}

export function RegistroLegalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openAddRep, setOpenAddRep] = useState(false);
  const [openAsociarExistente, setOpenAsociarExistente] = useState(false);
  const [tabPersonas, setTabPersonas] =
    useState<"reps" | "autorizados" | "tramitadores" | "empresas">("reps");
  const [openAddAutorizado, setOpenAddAutorizado] = useState(false);
  const [altaAutorizado, setAltaAutorizado] = useState<{ usuarioId: string; notificar: boolean } | null>(null);
  const [editarAutorizado, setEditarAutorizado] = useState<UsuarioAutorizadoItem | null>(null);
  const [cambiarAutorizado, setCambiarAutorizado] = useState<UsuarioAutorizadoItem | null>(null);
  const [reclasificarRep, setReclasificarRep] = useState<RepResumen | null>(null);
  const [reclasificarUA, setReclasificarUA] = useState<UsuarioAutorizadoItem | null>(null);
  /** Confirmación de la última operación sobre personas (reclasificación, alta). */
  const [avisoPersonas, setAvisoPersonas] = useState<string | null>(null);
  /**
   * Confirmación del cambio de estatus del registro. Siempre se muestra, haya
   * habido cascada o no: antes, un cambio sin relaciones que revocar no dejaba
   * ninguna señal y había que deducir el éxito de la insignia.
   */
  const [avisoEstatus, setAvisoEstatus] = useState<string | null>(null);
  const [cambiarRep, setCambiarRep] = useState<RepResumen | null>(null);
  const [vincularRep, setVincularRep] = useState<RepResumen | null>(null);
  const [cambiarUsuario, setCambiarUsuario] = useState<{ usuarioId: string; nombre: string; estatus: string } | null>(null);
  const [suplantar, setSuplantar] = useState<{ usuarioId: string; nombre: string } | null>(null);
  /**
   * Se pregunta por el rol del token REAL, no por el efectivo, porque es la
   * cuenta de atrás la que tiene la facultad. Y se oculta si ya hay una
   * suplantación viva: no se permiten encadenadas, así que ofrecerla sería
   * ofrecer algo que el backend va a rechazar.
   */
  const { suplantacion, refrescar: refrescarSuplantacion } = useSuplantacion();
  const puedeSuplantar = useEsAdminReal() && !suplantacion.activa;
  const [cambiarRl, setCambiarRl] = useState(false);
  const [subirDoc, setSubirDoc] = useState<RequisitoEstado | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [edicion, setEdicion] = useState<EdicionState | null>(null);
  const usuarioEsCalidad = useTieneRol(...ROLES_CALIDAD);
  const q = useQuery({
    queryKey: ["registro-legal", id],
    queryFn: async () => (await api.get<RegistroLegalDetalle>(`/api/registros-legales/${id}`)).data,
    enabled: !!id,
  });
  const completitudQ = useQuery({
    queryKey: ["registro-legal", id, "completitud"],
    queryFn: async () =>
      (await api.get<CompletitudDocumental>(`/api/registros-legales/${id}/completitud-documental`)).data,
    enabled: !!id,
  });
  const autorizadosQ = useQuery({
    queryKey: ["registro-legal", id, "usuarios-autorizados"],
    queryFn: async () =>
      (await api.get<UsuarioAutorizadoItem[]>(`/api/registros-legales/${id}/usuarios-autorizados`)).data,
    enabled: !!id,
  });
  // Relaciones Titular ↔ Tramitador en los dos sentidos (requerimiento
  // 2026-08-15 §8 y §9). Alimentan sus propias pestañas y contadores; nunca se
  // suman con los de las personas propias de la razón social (§10).
  const relacionesQ = useRelacionesTramitacion(id);
  // El impacto se pide al abrir el cambio de estatus, no al cargar la página:
  // simula la cascada y no tiene sentido calcularlo si nadie va a retirar nada.
  const impactoQ = useQuery({
    queryKey: ["registro-legal", id, "impacto-cambio-estatus"],
    queryFn: async () =>
      (await api.get<ImpactoCambioEstatus>(`/api/registros-legales/${id}/impacto-cambio-estatus`)).data,
    enabled: !!id && cambiarRl,
    staleTime: 0,
  });

  if (q.isLoading) return <DetailLoading />;
  if (q.error || !q.data) return <DetailError />;
  const d = q.data;
  const dom = d.domicilio;
  // Pestaña «Tramitadores externos»: solo cartas hacia un tramitador. Las
  // cartas internas (tramitador null) son el contenedor de los usuarios
  // autorizados y no se listan como tramitador.
  const cartasTramitador = d.cartasComoTitular.filter((c) => c.tramitadorRazonSocial != null);
  // Relaciones vigentes: alimentan los contadores de las pestañas y el selector
  // de representante que otorga una autorización.
  const repsActivos = d.representantes.filter((r) => r.estatus === "ACTIVO");
  const autorizadosActivos = (autorizadosQ.data ?? []).filter((ua) => ua.estatus === "ACTIVA");
  // Cada contador responde a un solo contexto (§10): representantes propios,
  // personal propio autorizado, gestorías que esta empresa autorizó y empresas
  // que la autorizaron a ella. No se mezclan ni se suman entre sí.
  const tramitadoresAutorizados = relacionesQ.data?.comoTitular ?? [];
  const empresasAtendidas = relacionesQ.data?.comoTramitador ?? [];
  // Los contadores cuentan relaciones que OPERAN. Las que siguen vigentes solo
  // en el papel —porque la razón social se retiró o la carta venció— se listan
  // aparte dentro de la pestaña y no inflan el número.
  const tramitadoresVigentes = tramitadoresAutorizados.filter((r) => r.relacionVigente).length;
  const empresasVigentes = empresasAtendidas.filter((r) => r.relacionVigente).length;
  const muestraEmpresasAtendidas = d.puedeActuarComoTramitador || empresasAtendidas.length > 0;
  const tabsPersonas: [typeof tabPersonas, string][] = [
    ["reps", `Representantes legales (${repsActivos.length})`],
    ["autorizados", `Usuarios autorizados (${autorizadosActivos.length})`],
    ["tramitadores", `Tramitadores autorizados (${tramitadoresVigentes})`],
  ];
  if (muestraEmpresasAtendidas) {
    tabsPersonas.push(["empresas", `Empresas atendidas (${empresasVigentes})`]);
  }

  return (
    <DetailLayout
      title={d.razonSocial}
      subtitle={
        <>
          <span className="font-mono">{d.rfc}</span> · {d.tipoPersona}
          {d.folioRegistro && <> · folio {d.folioRegistro}</>}
        </>
      }
      backTo="/tramites-legales"
      backLabel="Volver"
      badges={
        <>
          <StatusBadge tone={tonePorEstatus(d.estatus)}>{d.estatus}</StatusBadge>
          {d.puedeActuarComoTitular && (
            <StatusBadge tone="info">{cartasTramitador.length === 0 ? "Titular directo" : "Titular"}</StatusBadge>
          )}
          {d.puedeActuarComoTramitador && <StatusBadge tone="info">Tramitador</StatusBadge>}
          {cartasTramitador.map((c) => (
            <StatusBadge key={c.id} tone="neutral">Con tramitador: {c.tramitadorRazonSocial}</StatusBadge>
          ))}
          <button
            onClick={() => setCambiarRl(true)}
            disabled={ESTATUS_TERMINALES.has(d.estatus)}
            title={
              ESTATUS_TERMINALES.has(d.estatus)
                ? "El registro está en un estado final; recuperarlo requiere un procedimiento extraordinario."
                : undefined
            }
            className="cursor-pointer rounded border border-edge-strong px-2.5 py-0.5 text-xs font-medium text-fg-muted transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cambiar estatus
          </button>
          {usuarioEsCalidad && !edicion && (
            <button
              onClick={() => setEdicion(toEdicionState(d))}
              className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-0.5 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
            >
              <Pencil className="h-3 w-3" /> Editar
            </button>
          )}
        </>
      }
      rawData={d}
    >
      {avisoEstatus && (
        <div className="flex items-start gap-2 rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-3 text-sm text-estado-ok">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{avisoEstatus}</span>
          <button
            type="button"
            onClick={() => setAvisoEstatus(null)}
            className="cursor-pointer rounded p-0.5 transition hover:bg-estado-ok/10"
            aria-label="Cerrar aviso"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Por qué se retiró el registro, en la ficha y no solo en la bitácora. */}
      {ESTATUS_TERMINALES.has(d.estatus) && (
        <Section title="Retiro de operación">
          <Field label="Estatus" value={d.estatus} />
          <Field label="Fecha" value={formatDate(d.fechaActualizacion)} />
          <Field label="Realizado por" value={d.actualizadoPorCorreo} />
          <Field label="Motivo" span={3} value={d.motivoCambio} />
          <Field
            label="Nota"
            span={3}
            value={
              <p className="text-xs text-fg-subtle">
                {d.estatus === "CANCELADO"
                  ? "Cancelado: el registro no debía formar parte de la operación (duplicado, captura errónea o dato de prueba)."
                  : "Baja: la razón social tuvo operación válida y dejó de operar en SICEN."}{" "}
                Es un estado final; recuperarlo requiere un procedimiento extraordinario. Los
                documentos, las relaciones históricas y la bitácora se conservan.
              </p>
            }
          />
        </Section>
      )}

      {(d.estatus === "EN_REVISION_QA" || d.estatus === "OBSERVADO" || d.estatus === "RECHAZADO") && (
        <RevisionCalidadSection
          rlId={d.id}
          estatus={d.estatus}
          pasoActual={d.pasoActual ?? 1}
          fechaEnvioRevision={d.fechaEnvioRevision}
          solicitanteCorreo={null /* el detalle no trae correo del solicitante; usar futura mejora */}
          motivoRechazo={d.motivoRechazo}
          observaciones={d.observacionesQa}
          esCalidad={usuarioEsCalidad}
        />
      )}

      {edicion ? (
        <EdicionRLForm
          rl={d}
          state={edicion}
          onChange={setEdicion}
          onCancel={() => setEdicion(null)}
          onSaved={async () => {
            setEdicion(null);
            await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
            await qc.invalidateQueries({ queryKey: ["registro-legal", id, "completitud"] });
          }}
        />
      ) : (
        <>
          <Section title="Identidad">
            <Field label="Razón social" value={d.razonSocial} span={2} />
            <Field label="Nombre comercial" value={d.nombreComercial} />
            <Field label="Tipo de persona" value={d.tipoPersona} />
            <Field label="RFC" value={d.rfc} mono />
            <Field label="CURP" value={d.curp} mono />
            <Field label="Giro empresa" value={d.giroEmpresa} />
            <Field label="Correo" value={d.correoPrincipal} />
            <Field label="Teléfono" value={d.telefono} />
            <Field label="Página electrónica" value={d.paginaElectronica} />
          </Section>

          <Section title="Domicilio fiscal">
            <Field label="Calle" value={dom.calle} />
            <Field label="Número exterior" value={dom.numeroExterior} />
            <Field label="Número interior" value={dom.numeroInterior} />
            <Field label="Colonia" value={dom.colonia} />
            <Field label="Municipio" value={dom.municipio} />
            <Field label="Estado" value={dom.entidadFederativa} />
            <Field label="Código postal" value={dom.codigoPostal} mono />
            <Field label="País" value={dom.pais} />
          </Section>

          <Section title="Sectores habilitados">
            <Field
              label="Sectores"
              span={3}
              value={
                usuarioEsCalidad
                  ? <SectoresHabilitadosEditor rlId={d.id} habilitados={d.sectores} />
                  : d.sectores.length === 0
                    ? null
                    : (
                      <div className="flex flex-wrap gap-2">
                        {d.sectores.map((s) => (
                          <StatusBadge key={s.id} tone="neutral">
                            {s.nombre}
                          </StatusBadge>
                        ))}
                      </div>
                    )
              }
            />
          </Section>
        </>
      )}

      {completitudQ.data && (
        <Section
          title={`Documentos requeridos (${completitudQ.data.totalPresentes}/${completitudQ.data.totalRequeridos} obligatorios)`}
        >
          <Field
            label={completitudQ.data.completo ? "Estado" : "Faltantes"}
            value={
              completitudQ.data.completo ? (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-estado-ok">
                  <ShieldCheck className="h-4 w-4" /> Expediente completo
                </span>
              ) : (
                <span className="text-sm text-estado-warn">
                  Faltan {completitudQ.data.totalFaltantes} documento(s) obligatorio(s)
                </span>
              )
            }
            span={3}
          />
          <Field
            label="Lista"
            span={3}
            value={
              <ul className="space-y-1.5">
                {completitudQ.data.requisitos.map((r) => {
                  const necesitaReemplazo = r.presente && r.exigeCopiaCertificada && !r.cumpleCopiaCertificada;
                  const accionSubir = !r.presente ? "Subir" : (necesitaReemplazo || r.requiereRevision) ? "Subir nueva versión" : null;
                  return (
                    <li
                      key={r.tipoDocumentoId}
                      className={`flex flex-wrap items-start gap-2 rounded border p-2 text-sm ${
                        r.requiereRevision ? "border-estado-warn/25 bg-estado-warn-bg" : "bg-surface-2"
                      }`}
                    >
                      {r.presente ? (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-estado-ok" aria-label="presente" />
                      ) : (
                        <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-estado-warn" aria-label="faltante" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-fg">{r.tipoDocumentoNombre}</span>
                          {!r.obligatorio && <StatusBadge tone="neutral">Opcional</StatusBadge>}
                          {r.exigeCopiaCertificada && (
                            <StatusBadge tone="warn">Copia certificada original</StatusBadge>
                          )}
                          {necesitaReemplazo && (
                            <StatusBadge tone="warn">Versión vigente NO es certificada</StatusBadge>
                          )}
                          {r.requiereRevision && (
                            <StatusBadge tone="warn">Revisión sugerida</StatusBadge>
                          )}
                        </div>
                        {r.observacion && (
                          <div className="mt-0.5 text-xs text-fg-muted">{r.observacion}</div>
                        )}
                        {r.requiereRevision && r.motivoRevision && (
                          <div className="mt-0.5 text-xs text-estado-warn">
                            <span className="font-medium">Motivo:</span> {r.motivoRevision}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {r.presente && r.documentoLegalId && (
                          <button
                            onClick={() => setPreviewDocId(r.documentoLegalId)}
                            className="inline-flex cursor-pointer items-center gap-1 rounded border border-edge-strong px-2 py-1 text-xs font-medium text-fg-muted transition hover:bg-surface-2"
                          >
                            <Eye className="h-3 w-3" /> Ver
                          </button>
                        )}
                        {accionSubir && (
                          <button
                            onClick={() => setSubirDoc(r)}
                            className="cursor-pointer rounded border border-primary-strong px-2 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
                          >
                            {accionSubir}
                          </button>
                        )}
                        {r.requiereRevision && r.documentoLegalId && (
                          <button
                            onClick={async () => {
                              const comentario = window.prompt(
                                "Confirma que la versión vigente sigue válida tras el cambio. Comentario opcional:",
                                "",
                              );
                              if (comentario === null) return;
                              await api.post(`/api/documentos/${r.documentoLegalId}/marcar-revisado`, {
                                motivo: comentario.trim() || null,
                              });
                              await qc.invalidateQueries({ queryKey: ["registro-legal", id, "completitud"] });
                            }}
                            className="cursor-pointer rounded border border-estado-warn/40 px-2 py-1 text-xs font-medium text-estado-warn transition hover:bg-estado-warn-bg"
                          >
                            Marcar revisado
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            }
          />
        </Section>
      )}

      {/* Personas asociadas — tres pestañas (requerimiento RL 2026-07-15):
          la calidad jurídica (representantes) se separa del acceso operativo
          (usuarios autorizados) y de los tramitadores externos. */}
      <Section title="Personas asociadas a la razón social">
        {avisoPersonas && (
          <Field
            label="Resultado"
            span={3}
            value={
              <div className="flex items-start gap-2 rounded-lg border border-estado-ok/25 bg-estado-ok-bg p-3 text-sm text-estado-ok">
                <span className="flex-1">{avisoPersonas}</span>
                <button
                  type="button"
                  onClick={() => setAvisoPersonas(null)}
                  className="cursor-pointer rounded p-0.5 transition hover:bg-estado-ok/10"
                  aria-label="Cerrar aviso"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            }
          />
        )}
        <Field
          label="Categoría"
          span={3}
          value={
            <div className="flex flex-wrap gap-1 border-b pb-2">
              {/* Los contadores cuentan relaciones VIGENTES; el historial
                  (sustituidos/revocados) se sigue listando dentro de cada
                  pestaña. Cada uno mira un contexto distinto y no se suman
                  (§10): los dos primeros son personas de ESTA razón social;
                  los dos últimos, relaciones con otras razones sociales. */}
              {tabsPersonas.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTabPersonas(key)}
                  className={`cursor-pointer rounded-t px-3 py-1.5 text-sm transition ${
                    tabPersonas === key
                      ? "border border-b-0 bg-surface font-medium text-primary-strong"
                      : "text-fg-muted hover:bg-surface-2"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        />

        {tabPersonas === "reps" && (
          <>
            <Field
              label="Acciones"
              span={3}
              value={
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setOpenAddRep(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
                  >
                    <UserPlus className="h-4 w-4" />
                    Agregar representante
                  </button>
                  <button
                    onClick={() => setOpenAsociarExistente(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm text-primary-strong transition hover:bg-primary-tint"
                    title="Busca un representante ya capturado en otra empresa y asócialo aquí sin volver a capturarlo"
                  >
                    <Link2 className="h-4 w-4" />
                    Asociar representante existente
                  </button>
                  <button
                    onClick={() => setOpenAddUser(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm text-primary-strong transition hover:bg-primary-tint"
                    title="Crea o vincula cuenta SICEN al representante (login + correo de bienvenida). Si la persona solo operará trámites, usa la pestaña «Usuarios autorizados»."
                  >
                    <UserPlus className="h-4 w-4" />
                    Asociar usuario SICEN como representante
                  </button>
                </div>
              }
            />
            <Field
              label="Lista"
              span={3}
              value={
                d.representantes.length === 0 ? (
                  <span className="text-sm text-fg-faint">Sin representantes legales.</span>
                ) : (
                  <ul className="space-y-2">
                    {d.representantes.map((r) => {
                      const repActivo = r.estatus === "ACTIVO";
                      return (
                        <li key={r.id} className="flex flex-wrap items-center gap-2 rounded border bg-surface-2 p-2">
                          <div className="min-w-0 flex-1">
                            <Link to={`/representantes-legales/${r.id}`} className="text-sm font-medium text-primary-strong hover:underline">
                              {r.nombreCompleto}
                            </Link>
                            {r.correo && <span className="ml-2 text-xs text-fg-subtle">{r.correo}</span>}
                            {(r.tipoRepresentacion || r.instrumentoNumero) && (
                              <span className="block text-xs text-fg-subtle">
                                {r.tipoRepresentacion ? TIPO_REPRESENTACION_LABEL[r.tipoRepresentacion] ?? r.tipoRepresentacion : ""}
                                {r.instrumentoNumero && ` · Instrumento ${r.instrumentoNumero}`}
                              </span>
                            )}
                          </div>
                          <StatusBadge tone={tonePorEstatus(r.estatus)}>{r.estatus}</StatusBadge>
                          {!r.instrumentoPoderDocumentoId && (
                            <StatusBadge tone="warn">sin instrumento</StatusBadge>
                          )}
                          {!r.usuarioId && <StatusBadge tone="warn">sin cuenta</StatusBadge>}
                          <div className="flex gap-1">
                            {usuarioEsCalidad && repActivo && !r.usuarioId && (
                              <button
                                onClick={() => setVincularRep(r)}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
                                title="Crear o vincular la cuenta SICEN (login) de este representante"
                              >
                                <Link2 className="h-3 w-3" />
                                Vincular cuenta SICEN
                              </button>
                            )}
                            {usuarioEsCalidad && repActivo && (
                              <button
                                onClick={() => setReclasificarRep(r)}
                                disabled={!r.usuarioId || repsActivos.length < 2}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint disabled:cursor-not-allowed disabled:opacity-50"
                                title={
                                  !r.usuarioId
                                    ? "Para autorizarlo, primero usa «Vincular cuenta SICEN»"
                                    : repsActivos.length < 2
                                      ? "Se necesita otro representante legal vigente que otorgue la autorización"
                                      : "Autorizar para operar trámites; puede conservar su representación legal"
                                }
                              >
                                + Autorizar para trámites
                              </button>
                            )}
                            <button
                              onClick={() => setCambiarRep(r)}
                              className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs transition ${
                                repActivo
                                  ? "border-estado-warn/25 text-estado-warn hover:bg-estado-warn-bg"
                                  : "border-estado-ok/25 text-estado-ok hover:bg-estado-ok-bg"
                              }`}
                              title={repActivo ? "Revocar representación" : "Reactivar"}
                            >
                              {repActivo ? <PowerOff className="h-3 w-3" /> : <Power className="h-3 w-3" />}
                              {repActivo ? "Revocar" : "Reactivar"}
                            </button>
                            {r.usuarioId && (
                              <button
                                onClick={() =>
                                  setCambiarUsuario({ usuarioId: r.usuarioId!, nombre: r.nombreCompleto, estatus: "ACTIVO" })
                                }
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-err/25 px-2 py-1 text-xs text-estado-err transition hover:bg-estado-err-bg"
                                title="Cambiar estatus del usuario (login)"
                              >
                                <PowerOff className="h-3 w-3" />
                                Estatus usuario
                              </button>
                            )}
                            {/* Reservado a ADMIN_SICEN. Para el resto de roles no
                                se muestra deshabilitada: simplemente no existe. */}
                            {puedeSuplantar && r.usuarioId && (
                              <button
                                onClick={() =>
                                  setSuplantar({ usuarioId: r.usuarioId!, nombre: r.nombreCompleto })
                                }
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-warn/40 px-2 py-1 text-xs text-estado-warn transition hover:bg-estado-warn-bg"
                                title="Navegar SICEN como esta persona para validar su experiencia"
                              >
                                <UserCheck className="h-3 w-3" />
                                Suplantar usuario
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )
              }
            />
          </>
        )}

        {tabPersonas === "autorizados" && (
          <>
            <Field
              label="Acciones"
              span={3}
              value={
                <div className="space-y-2">
                  <button
                    onClick={() => setOpenAddAutorizado(true)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
                  >
                    <UserPlus className="h-4 w-4" />
                    Agregar usuario autorizado
                  </button>
                  <p className="text-xs text-fg-subtle">
                    Personal interno de la empresa autorizado para operar solicitudes de certificación
                    <strong> por sector</strong>. No aparece como representante legal ni administra el
                    Registro Legal. El documento de autorización (carta poder / poder especial) es opcional.
                  </p>
                </div>
              }
            />
            <Field
              label="Lista"
              span={3}
              value={
                autorizadosQ.isLoading ? (
                  <span className="text-sm text-fg-faint">Cargando…</span>
                ) : (autorizadosQ.data ?? []).length === 0 ? (
                  <span className="text-sm text-fg-faint">Sin usuarios autorizados.</span>
                ) : (
                  <ul className="space-y-2">
                    {(autorizadosQ.data ?? []).map((ua) => {
                      const activa = ua.estatus === "ACTIVA";
                      const terminal = ua.estatus === "REVOCADA" || ua.estatus === "SUSTITUIDA";
                      return (
                        <li key={ua.asociacionId} className="flex flex-wrap items-center gap-2 rounded border bg-surface-2 p-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-fg">{ua.nombreCompleto || ua.correo}</span>
                            {ua.correo && <span className="ml-2 text-xs text-fg-subtle">{ua.correo}</span>}
                            <span className="block text-xs text-fg-subtle">
                              {TIPO_AUTORIZACION_LABEL[ua.tipoAutorizacion] ?? ua.tipoAutorizacion}
                              {ua.puestoArea && ` · ${ua.puestoArea}`}
                              {ua.otorganteNombre && ` · otorga: ${ua.otorganteNombre}`}
                            </span>
                            <span className="block text-xs text-fg-subtle">
                              Sectores: {ua.sectoresCodigos.length > 0 ? ua.sectoresCodigos.join(", ") : "—"}
                              {" · Vigencia: "}
                              {ua.vigenciaFin ? `hasta ${formatDate(ua.vigenciaFin)}` : "sin vencimiento"}
                            </span>
                          </div>
                          <StatusBadge tone={activa ? "ok" : terminal ? "warn" : "neutral"}>
                            {ESTATUS_AUTORIZACION_LABEL[ua.estatus] ?? ua.estatus}
                          </StatusBadge>
                          {!terminal && (
                            <div className="flex gap-1">
                              {usuarioEsCalidad && (
                                <button
                                  onClick={() => setReclasificarUA(ua)}
                                  className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
                                  title="Reclasificar como representante legal (conserva cuenta e historial)"
                                >
                                  → Representante
                                </button>
                              )}
                              <button
                                onClick={() => setEditarAutorizado(ua)}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong/40 px-2 py-1 text-xs text-primary-strong transition hover:bg-primary-tint"
                              >
                                <Pencil className="h-3 w-3" />
                                Editar
                              </button>
                              <button
                                onClick={() => setCambiarAutorizado(ua)}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-warn/25 px-2 py-1 text-xs text-estado-warn transition hover:bg-estado-warn-bg"
                                title="Inactivar, reactivar o revocar la autorización"
                              >
                                <PowerOff className="h-3 w-3" />
                                Estatus
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )
              }
            />
          </>
        )}

        {/* §9 — vista desde la empresa titular: qué gestorías puede usar, con
            qué personal, qué instrumento lo acredita y hasta cuándo. */}
        {tabPersonas === "tramitadores" && (
          <>
            <Field
              label="Descripción"
              span={3}
              value={
                <p className="text-xs text-fg-subtle">
                  Empresas tramitadoras que esta razón social autorizó por carta poder, y el personal
                  concreto de cada una que puede operar sus trámites. La autorización vive en la
                  relación: la misma persona puede estar autorizada aquí y no en otra empresa.
                </p>
              }
            />
            <Field
              label="Relaciones"
              span={3}
              value={
                relacionesQ.isLoading ? (
                  <span className="text-sm text-fg-faint">Cargando…</span>
                ) : (
                  <RelacionesTramitacionPanel
                    relaciones={tramitadoresAutorizados}
                    perspectiva="TITULAR"
                    puedeAdministrar={usuarioEsCalidad}
                    titularId={id}
                    titularRazonSocial={d.razonSocial}
                    onVerDocumento={setPreviewDocId}
                    onCambio={relacionesQ.invalidar}
                  />
                )
              }
            />
          </>
        )}

        {/* §8 — vista desde la empresa tramitadora: para qué empresas titulares
            está autorizada y quién de su personal opera en cada una. */}
        {tabPersonas === "empresas" && (
          <>
            <Field
              label="Descripción"
              span={3}
              value={
                <p className="text-xs text-fg-subtle">
                  Empresas titulares que autorizaron a esta razón social a tramitar en su nombre. El
                  personal listado en cada relación es el autorizado <strong>por esa empresa</strong>;
                  no es el personal de la gestoría ni sus representantes legales, que se administran en
                  las otras pestañas.
                </p>
              }
            />
            <Field
              label="Relaciones"
              span={3}
              value={
                relacionesQ.isLoading ? (
                  <span className="text-sm text-fg-faint">Cargando…</span>
                ) : (
                  <RelacionesTramitacionPanel
                    relaciones={empresasAtendidas}
                    perspectiva="TRAMITADOR"
                    puedeAdministrar={usuarioEsCalidad}
                    onVerDocumento={setPreviewDocId}
                    onCambio={relacionesQ.invalidar}
                  />
                )
              }
            />
          </>
        )}
      </Section>

      <UserSearchOrCreate
        open={openAddUser}
        title={`Agregar usuario a ${d.razonSocial}`}
        onClose={() => setOpenAddUser(false)}
        onAsociar={async (usuarioId, notificar) => {
          await api.post(`/api/registros-legales/${d.id}/usuarios`, { usuarioId, notificar });
          await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
        }}
      />

      {/* Vincular cuenta desde la fila del representante sin cuenta: mismo
          endpoint que el alta general; el backend vincula a la ficha existente
          cuando el correo coincide, por eso se precarga el de la ficha. */}
      <UserSearchOrCreate
        open={!!vincularRep}
        title={`Vincular cuenta SICEN a ${vincularRep?.nombreCompleto ?? ""}`}
        correoInicial={vincularRep?.correo}
        hint={
          vincularRep?.correo
            ? `Con el correo de la ficha (${vincularRep.correo}) la cuenta se vincula a este representante; con un correo distinto se crearía un representante adicional.`
            : "La ficha no tiene correo: la cuenta se creará como un representante adicional."
        }
        onClose={() => setVincularRep(null)}
        onAsociar={async (usuarioId, notificar) => {
          await api.post(`/api/registros-legales/${d.id}/usuarios`, { usuarioId, notificar });
          await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
        }}
      />

      {/* Alta de usuario autorizado: primero busca/crea la cuenta SICEN,
          después captura la autorización (sectores, facultad, otorgante). */}
      <UserSearchOrCreate
        open={openAddAutorizado}
        title={`Autorizar usuario para trámites de ${d.razonSocial}`}
        onClose={() => setOpenAddAutorizado(false)}
        onAsociar={async (usuarioId, notificar) => {
          setOpenAddAutorizado(false);
          setAltaAutorizado({ usuarioId, notificar });
        }}
      />

      {altaAutorizado && (
        <AltaUsuarioAutorizadoModal
          registroLegalId={d.id}
          razonSocial={d.razonSocial}
          usuarioId={altaAutorizado.usuarioId}
          notificar={altaAutorizado.notificar}
          representantes={repsActivos}
          onClose={() => setAltaAutorizado(null)}
          onSaved={async () => {
            setAltaAutorizado(null);
            setAvisoPersonas("Usuario autorizado correctamente para operar trámites.");
            await qc.invalidateQueries({ queryKey: ["registro-legal", id, "usuarios-autorizados"] });
          }}
        />
      )}

      {/* Representante → también autorizado para trámites: mismo formulario de
          alcance sobre la cuenta del representante. Conservar o no la
          representación legal se elige dentro del modal (§4). */}
      {reclasificarRep && (
        <AltaUsuarioAutorizadoModal
          registroLegalId={d.id}
          razonSocial={d.razonSocial}
          reclasificarRepresentante={{ id: reclasificarRep.id, nombre: reclasificarRep.nombreCompleto }}
          notificar
          representantes={repsActivos.filter((r) => r.id !== reclasificarRep.id)}
          onClose={() => setReclasificarRep(null)}
          onSaved={async () => {
            const nombre = reclasificarRep.nombreCompleto;
            setReclasificarRep(null);
            setAvisoPersonas(`${nombre} quedó autorizado para operar trámites de ${d.razonSocial}.`);
            setTabPersonas("autorizados");
            await Promise.all([
              qc.invalidateQueries({ queryKey: ["registro-legal", id] }),
              qc.invalidateQueries({ queryKey: ["registro-legal", id, "usuarios-autorizados"] }),
            ]);
          }}
        />
      )}

      {reclasificarUA && (
        <ReclasificarARepresentanteModal
          item={reclasificarUA}
          onClose={() => setReclasificarUA(null)}
          onSaved={async () => {
            const nombre = reclasificarUA.nombreCompleto || reclasificarUA.correo;
            setReclasificarUA(null);
            setAvisoPersonas(`${nombre} quedó reclasificado correctamente como representante legal.`);
            setTabPersonas("reps");
            await Promise.all([
              qc.invalidateQueries({ queryKey: ["registro-legal", id] }),
              qc.invalidateQueries({ queryKey: ["registro-legal", id, "usuarios-autorizados"] }),
            ]);
          }}
        />
      )}

      {editarAutorizado && (
        <EditarUsuarioAutorizadoModal
          item={editarAutorizado}
          representantes={repsActivos}
          onClose={() => setEditarAutorizado(null)}
          onSaved={async () => {
            setEditarAutorizado(null);
            await qc.invalidateQueries({ queryKey: ["registro-legal", id, "usuarios-autorizados"] });
          }}
        />
      )}

      <CambiarEstatusModal
        open={!!cambiarAutorizado}
        title={cambiarAutorizado ? `Estatus de la autorización de ${cambiarAutorizado.nombreCompleto || cambiarAutorizado.correo}` : ""}
        estatusActual={cambiarAutorizado?.estatus ?? ""}
        opciones={[
          { value: "ACTIVA", label: "Activa", description: "El usuario puede operar trámites en sus sectores." },
          { value: "SUSPENDIDA", label: "Inactiva", description: "Pausa reversible; el usuario deja de operar por esta empresa." },
          { value: "REVOCADA", label: "Revocada", description: "Terminal. Conserva el historial para auditoría (RN-08)." },
        ]}
        onCancel={() => setCambiarAutorizado(null)}
        onConfirm={async (nuevoEstatus, motivo) => {
          await api.post(`/api/usuarios-autorizados/${cambiarAutorizado!.asociacionId}/estatus`, {
            estatus: nuevoEstatus,
            motivo,
          });
          setCambiarAutorizado(null);
          await qc.invalidateQueries({ queryKey: ["registro-legal", id, "usuarios-autorizados"] });
        }}
      />

      {openAsociarExistente && (
        <AsociarRepresentanteExistenteModal
          registroLegalId={d.id}
          razonSocial={d.razonSocial}
          onClose={() => setOpenAsociarExistente(false)}
          onSaved={async () => {
            setOpenAsociarExistente(false);
            await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
          }}
        />
      )}

      {openAddRep && (
        <AltaRepresentanteModal
          registroLegalId={d.id}
          razonSocial={d.razonSocial}
          onClose={() => setOpenAddRep(false)}
          onSaved={async () => {
            setOpenAddRep(false);
            await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
          }}
        />
      )}

      <CambiarEstatusModal
        open={!!cambiarRep}
        title={cambiarRep ? `Cambiar estatus de ${cambiarRep.nombreCompleto}` : ""}
        estatusActual={cambiarRep?.estatus ?? ""}
        opciones={[
          { value: "ACTIVO", label: "Activo", description: "Restaurar la representación." },
          { value: "REVOCADO", label: "Revocado", description: "Revoca la autorización; preserva histórico." },
          { value: "SUSTITUIDO", label: "Sustituido", description: "El representante fue reemplazado por otro." },
        ]}
        onCancel={() => setCambiarRep(null)}
        onConfirm={async (nuevoEstatus, motivo) => {
          await api.put(`/api/representantes-legales/${cambiarRep!.id}/estatus`, { nuevoEstatus, motivo });
          setCambiarRep(null);
          await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
        }}
      />

      <CambiarEstatusModal
        open={!!cambiarUsuario}
        title={cambiarUsuario ? `Cambiar estatus del usuario de ${cambiarUsuario.nombre}` : ""}
        estatusActual={cambiarUsuario?.estatus ?? "ACTIVO"}
        opciones={[
          { value: "ACTIVO", label: "Activo", description: "Cuenta operativa; puede iniciar sesión." },
          { value: "SUSPENDIDO", label: "Suspendido", description: "Bloqueo temporal; conserva histórico." },
          { value: "BAJA", label: "Baja", description: "Cuenta cerrada; conserva histórico y auditoría." },
        ]}
        onCancel={() => setCambiarUsuario(null)}
        onConfirm={async (nuevoEstatus, motivo) => {
          await api.put(`/api/iam/usuarios/${cambiarUsuario!.usuarioId}/estatus`, { nuevoEstatus, motivo });
          setCambiarUsuario(null);
          await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
        }}
      />

      <CambiarEstatusModal
        open={cambiarRl}
        title={`Cambiar estatus de ${d.razonSocial}`}
        estatusActual={d.estatus}
        opciones={[
          { value: "ACTIVO", label: "Activo", description: "Operativo; reanuda visibilidad y trámites." },
          {
            value: "SUSPENDIDO",
            label: "Suspendido",
            description: "Pausa temporal reversible; mantiene representantes y asociaciones.",
          },
          {
            value: "BAJA",
            label: "Baja",
            description:
              "Terminal e irreversible. Para una razón social que tuvo operación válida y deja de operar en SICEN. Revoca sus representantes y autorizaciones vigentes.",
          },
          {
            value: "CANCELADO",
            label: "Cancelado",
            description:
              "Terminal e irreversible. Para registros que no deben formar parte de la operación: duplicados, capturas erróneas o datos de prueba. Revoca sus representantes y autorizaciones vigentes.",
          },
        ]}
        aviso={(nuevoEstatus) =>
          ESTATUS_TERMINALES.has(nuevoEstatus) ? (
            <AvisoImpactoTerminal
              nuevoEstatus={nuevoEstatus}
              impacto={impactoQ.data}
              cargando={impactoQ.isLoading}
            />
          ) : null
        }
        onCancel={() => setCambiarRl(false)}
        onConfirm={async (nuevoEstatus, motivo) => {
          const terminal = ESTATUS_TERMINALES.has(nuevoEstatus);
          const seDeshabilitan = (impactoQ.data?.usuariosSinOtraRelacion ?? []).filter(
            (u) => !u.protegidoPorRol,
          ).length;
          if (
            terminal &&
            !window.confirm(
              `${nuevoEstatus} es un estado final: el registro no podrá volver a un estatus operativo.` +
                (seDeshabilitan > 0
                  ? `\n\nAdemás, ${seDeshabilitan} cuenta(s) SICEN quedarán deshabilitadas.`
                  : "") +
                `\n\n¿Confirmas?`,
            )
          ) {
            return;
          }
          const resp = await api.put<{
            representantesRevocados: number;
            asociacionesRevocadas: number;
            usuariosAfectados: number;
            usuariosBajados: number;
            usuariosProtegidosPorRol: number;
          }>(`/api/registros-legales/${id}/estatus`, { nuevoEstatus, motivo });
          setCambiarRl(false);
          const r = resp.data;
          const protegidas =
            r.usuariosProtegidosPorRol > 0
              ? ` ${r.usuariosProtegidosPorRol} cuenta(s) conservaron acceso por su rol administrativo pese a quedarse sin relaciones.`
              : "";
          const cascada =
            r.representantesRevocados + r.asociacionesRevocadas + r.usuariosAfectados > 0
              ? ` Se revocaron ${r.representantesRevocados} representante(s) y ${r.asociacionesRevocadas} autorización(es); ` +
                `${r.usuariosAfectados} persona(s) afectada(s), ${r.usuariosBajados} cuenta(s) dadas de baja.${protegidas}`
              : " No había representantes ni autorizaciones vigentes que revocar.";
          setAvisoEstatus(`${d.razonSocial} quedó en estatus ${nuevoEstatus}.${cascada}`);
          await qc.invalidateQueries({ queryKey: ["registro-legal", id] });
        }}
      />

      {suplantar && (
        <SuplantarUsuarioModal
          objetivo={suplantar}
          onClose={() => setSuplantar(null)}
          onIniciada={async () => {
            setSuplantar(null);
            await refrescarSuplantacion();
            // Todo lo cacheado se resolvió con la identidad del administrador;
            // a partir de ahora la sesión es otra y nada de eso vale.
            await qc.invalidateQueries();
          }}
        />
      )}

      {previewDocId && (
        <DocumentoPreviewModal
          documentoId={previewDocId}
          onClose={() => setPreviewDocId(null)}
        />
      )}

      {subirDoc && id && (
        <UploadDocumentoModal
          open={true}
          title={
            subirDoc.presente
              ? `Nueva versión: ${subirDoc.tipoDocumentoNombre}`
              : `Subir: ${subirDoc.tipoDocumentoNombre}`
          }
          contextoTipo="REGISTRO_LEGAL"
          contextoId={id}
          tipoCodigoFijo={subirDoc.tipoDocumentoCodigo}
          documentoExistenteId={subirDoc.documentoLegalId ?? undefined}
          esCopiaCertificadaInicial={subirDoc.exigeCopiaCertificada}
          pedirCopiaCertificada={subirDoc.exigeCopiaCertificada}
          onClose={() => setSubirDoc(null)}
          onUploaded={async () => {
            setSubirDoc(null);
            await Promise.all([
              qc.invalidateQueries({ queryKey: ["registro-legal", id, "completitud"] }),
              qc.invalidateQueries({ queryKey: ["registro-legal", id] }),
            ]);
          }}
        />
      )}

      <Section title="Información del registro">
        <Field label="Estatus" value={d.estatus} />
        <Field label="Tiene filiales" value={d.tieneFiliales ? "Sí" : "No"} />
        <Field label="Fecha de alta" value={formatDate(d.fechaCreacion, true)} />
        <Field label="Última actualización" value={formatDate(d.fechaActualizacion, true)} />
      </Section>
    </DetailLayout>
  );
}

interface SectorOption { id: string; codigo: string; nombre: string; }

interface EdicionState {
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  razonSocial: string;
  nombreComercial: string;
  curp: string;
  giroEmpresa: string;
  correoPrincipal: string;
  telefono: string;
  paginaElectronica: string;
  domicilio: Domicilio;
  consentimientoAvisoPrivacidad: boolean;
  tieneFiliales: boolean;
  sectoresAutorizados: string[];
  motivoCambio: string;
}

function toEdicionState(d: RegistroLegalDetalle): EdicionState {
  return {
    puedeActuarComoTitular: d.puedeActuarComoTitular,
    puedeActuarComoTramitador: d.puedeActuarComoTramitador,
    razonSocial: d.razonSocial,
    nombreComercial: d.nombreComercial ?? "",
    curp: d.curp ?? "",
    giroEmpresa: d.giroEmpresa ?? "",
    correoPrincipal: d.correoPrincipal,
    telefono: d.telefono,
    paginaElectronica: d.paginaElectronica ?? "",
    domicilio: { ...d.domicilio, numeroInterior: d.domicilio.numeroInterior ?? "" },
    consentimientoAvisoPrivacidad: true,
    tieneFiliales: d.tieneFiliales,
    sectoresAutorizados: d.sectores.map((s) => s.id),
    motivoCambio: "",
  };
}

function EdicionRLForm({
  rl,
  state,
  onChange,
  onCancel,
  onSaved,
}: {
  rl: RegistroLegalDetalle;
  state: EdicionState;
  onChange: (s: EdicionState) => void;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qSectores = useQuery<SectorOption[]>({
    queryKey: ["catalogo-sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
  });

  const setField = <K extends keyof EdicionState>(k: K, v: EdicionState[K]) =>
    onChange({ ...state, [k]: v });
  const setDom = (patch: Partial<Domicilio>) =>
    onChange({ ...state, domicilio: { ...state.domicilio, ...patch } });
  const toggleSector = (id: string) => {
    const has = state.sectoresAutorizados.includes(id);
    onChange({
      ...state,
      sectoresAutorizados: has
        ? state.sectoresAutorizados.filter((s) => s !== id)
        : [...state.sectoresAutorizados, id],
    });
  };

  const guardar = async () => {
    setError(null);
    if (!state.razonSocial.trim()) return setError("La razón social es obligatoria.");
    if (!state.correoPrincipal.trim()) return setError("El correo es obligatorio.");
    if (!state.telefono.trim()) return setError("El teléfono es obligatorio.");
    if (!state.motivoCambio.trim()) return setError("El motivo del cambio es obligatorio (queda en bitácora).");
    setSaving(true);
    try {
      await api.put(`/api/registros-legales/${rl.id}`, {
        ...state,
        nombreComercial: state.nombreComercial.trim() || null,
        curp: state.curp.trim() || null,
        giroEmpresa: state.giroEmpresa.trim() || null,
        paginaElectronica: state.paginaElectronica.trim() || null,
        domicilio: {
          ...state.domicilio,
          numeroInterior: state.domicilio.numeroInterior?.trim() || null,
        },
      });
      await onSaved();
    } catch (e: unknown) {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border-2 border-primary-strong bg-primary-tint shadow-card">
      <div className="flex items-center justify-between border-b border-primary-strong/30 px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-primary-strong">
          Editando registro legal
        </h2>
        <span className="text-xs text-fg-subtle">
          Inmutables: RFC, tipo de persona, estatus
        </span>
      </div>

      <div className="space-y-4 p-4">
        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">Identidad</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input label="Razón social *" value={state.razonSocial} onChange={(v) => setField("razonSocial", v)} span={2} />
            <Input label="Nombre comercial" value={state.nombreComercial} onChange={(v) => setField("nombreComercial", v)} />
            <Input label="CURP" value={state.curp} onChange={(v) => setField("curp", v.toUpperCase())} mono />
            <Input label="Giro empresa" value={state.giroEmpresa} onChange={(v) => setField("giroEmpresa", v)} />
            <Input label="Correo *" type="email" value={state.correoPrincipal} onChange={(v) => setField("correoPrincipal", v)} />
            <Input label="Teléfono *" value={state.telefono} onChange={(v) => setField("telefono", v)} />
            <Input label="Página electrónica" value={state.paginaElectronica} onChange={(v) => setField("paginaElectronica", v)} />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">Banderas</legend>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={state.puedeActuarComoTitular} onChange={(e) => setField("puedeActuarComoTitular", e.target.checked)} />
              Puede actuar como titular
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={state.puedeActuarComoTramitador} onChange={(e) => setField("puedeActuarComoTramitador", e.target.checked)} />
              Puede actuar como tramitador
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={state.tieneFiliales} onChange={(e) => setField("tieneFiliales", e.target.checked)} />
              Tiene filiales
            </label>
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">Domicilio fiscal</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input label="Calle *" value={state.domicilio.calle} onChange={(v) => setDom({ calle: v })} />
            <Input label="Número exterior *" value={state.domicilio.numeroExterior} onChange={(v) => setDom({ numeroExterior: v })} />
            <Input label="Número interior" value={state.domicilio.numeroInterior ?? ""} onChange={(v) => setDom({ numeroInterior: v })} />
            <Input label="Colonia *" value={state.domicilio.colonia} onChange={(v) => setDom({ colonia: v })} />
            <Input label="Municipio *" value={state.domicilio.municipio} onChange={(v) => setDom({ municipio: v })} />
            <Input label="Estado *" value={state.domicilio.entidadFederativa} onChange={(v) => setDom({ entidadFederativa: v })} />
            <Input label="Código postal *" value={state.domicilio.codigoPostal} onChange={(v) => setDom({ codigoPostal: v })} mono />
            <Input label="País" value={state.domicilio.pais} onChange={(v) => setDom({ pais: v })} />
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">Sectores autorizados</legend>
          {qSectores.isLoading ? (
            <div className="text-xs text-fg-subtle">Cargando catálogo…</div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {(qSectores.data ?? []).map((s) => (
                <label key={s.id} className="inline-flex cursor-pointer items-center gap-2 rounded border bg-surface px-3 py-1.5 text-sm transition hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={state.sectoresAutorizados.includes(s.id)}
                    onChange={() => toggleSector(s.id)}
                  />
                  {s.nombre}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Motivo del cambio <span className="text-estado-err">*</span>
          </legend>
          <textarea
            value={state.motivoCambio}
            onChange={(e) => setField("motivoCambio", e.target.value)}
            rows={2}
            placeholder="Queda en bitácora. Ej. 'Calidad complementa información faltante después del envío a revisión.'"
            className="w-full rounded border border-edge-strong px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            disabled={saving}
          />
        </fieldset>

        {error && (
          <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t bg-surface-2 px-4 py-3">
        <button
          onClick={onCancel}
          disabled={saving}
          className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          onClick={guardar}
          disabled={saving}
          className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </section>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  span = 1,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email";
  span?: 1 | 2 | 3;
  mono?: boolean;
}) {
  const colSpan = span === 3 ? "sm:col-span-2 lg:col-span-3" : span === 2 ? "sm:col-span-2" : "";
  return (
    <div className={colSpan}>
      <label className="text-xs uppercase tracking-wide text-fg-subtle">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-0.5 w-full rounded border border-edge-strong px-2 py-1.5 text-sm focus:border-primary focus:outline-none ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

/**
 * Alta de un representante legal sin obligar a crear cuenta SICEN. Usado por
 * Calidad desde el detalle del RL para capturar a un representante con los
 * datos del Ítem 2 (nombres divididos, domicilio SEPOMEX, extensión).
 */
function AltaRepresentanteModal({
  registroLegalId,
  razonSocial,
  onClose,
  onSaved,
}: {
  registroLegalId: string;
  razonSocial: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [form, setForm] = useState<RepresentanteBorrador>({
    nombres: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    nombreCompleto: "",
    rfc: "",
    curp: "",
    correo: "",
    telefono: "",
    telefonoExtension: "",
    domicilioCalle: "",
    domicilioNumeroExterior: "",
    domicilioNumeroInterior: "",
    domicilioCodigoPostal: "",
    domicilioColonia: "",
    domicilioMunicipio: "",
    domicilioEntidadFederativa: "",
    domicilioPais: "MEX",
    domicilioLibre: "",
    consentimientoNotificacionElectronica: false,
    instrumentoPoderDescripcion: "",
    vigenciaInicio: new Date().toISOString(),
    vigenciaFin: null,
  });
  // V152 — requerimiento RL 2026-07-15: facultad y referencia del instrumento.
  const [tipoRepresentacion, setTipoRepresentacion] = useState("");
  const [instrumentoNumero, setInstrumentoNumero] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const normalizado = normalizarRepresentante(form);
      await api.post(`/api/representantes-legales`, {
        registroLegalId,
        ...normalizado,
        consentimientoNotificacionElectronica: form.consentimientoNotificacionElectronica,
        vigenciaInicio: form.vigenciaInicio ?? new Date().toISOString(),
        tipoRepresentacion: tipoRepresentacion || null,
        instrumentoNumero: instrumentoNumero.trim() || null,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const validacion = validarRepresentante(form);
    if (validacion) {
      setErr(validacion);
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Agregar representante a {razonSocial}
          </h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5 p-4">
          <p className="rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
            Este alta NO crea cuenta SICEN para el representante. Si además quieres darle login, usa
            “Asociar usuario SICEN” en la sección de representantes.
          </p>

          <RepresentanteFormFields r={form} setR={setForm} />

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de representación</span>
              <select
                value={tipoRepresentacion}
                onChange={(e) => setTipoRepresentacion(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              >
                <option value="">— Sin especificar —</option>
                <option value="REPRESENTANTE_LEGAL">Representante legal</option>
                <option value="APODERADO_GENERAL">Apoderado general</option>
                <option value="APODERADO_ESPECIAL">Apoderado especial</option>
                <option value="OTRO">Otro</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-fg-muted">Número o referencia del instrumento</span>
              <input
                value={instrumentoNumero}
                onChange={(e) => setInstrumentoNumero(e.target.value)}
                className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
                placeholder="p. ej. Escritura 12,345"
              />
            </label>
          </div>

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Guardar representante"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface RepBusquedaItem {
  id: string;
  registroLegalId: string;
  registroRazonSocial: string | null;
  registroRfc: string | null;
  nombreCompleto: string;
  correo: string | null;
  rfc: string | null;
  usuarioId: string | null;
  tieneInstrumento: boolean;
  estatus: string;
}

/**
 * Flujo «Asociar representante existente»: busca por nombre o correo entre
 * los representantes activos de todo el sistema y asocia el seleccionado a la
 * razón social actual, sin crear usuarios nuevos. El backend valida
 * duplicados (mismo correo o misma cuenta ya activos en esta empresa).
 */
function AsociarRepresentanteExistenteModal({
  registroLegalId,
  razonSocial,
  onClose,
  onSaved,
}: {
  registroLegalId: string;
  razonSocial: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState<RepBusquedaItem[] | null>(null);
  const [seleccionado, setSeleccionado] = useState<RepBusquedaItem | null>(null);
  const [notificar, setNotificar] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const buscar = async () => {
    if (q.trim().length < 3) {
      setErr("Escribe al menos 3 caracteres del nombre o correo.");
      return;
    }
    setErr(null);
    setBuscando(true);
    setSeleccionado(null);
    try {
      const { data } = await api.get<RepBusquedaItem[]>(
        `/api/representantes-legales/buscar?q=${encodeURIComponent(q.trim())}`,
      );
      setResultados(data);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    } finally {
      setBuscando(false);
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/api/registros-legales/${registroLegalId}/representantes/asociar-existente`, {
        representanteId: seleccionado!.id,
        notificar,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Asociar representante existente a {razonSocial}
          </h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <p className="rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
            Busca a la persona entre los representantes ya capturados en el sistema. Se asociará a esta
            razón social con sus datos actuales y, si tiene cuenta SICEN, conservará su acceso, roles y
            permisos — no se crea ningún usuario nuevo. El instrumento de poder se carga aparte, porque es
            específico de cada empresa.
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  buscar();
                }
              }}
              placeholder="Nombre o correo del representante"
              className="flex-1 rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              autoFocus
            />
            <button
              type="button"
              onClick={buscar}
              disabled={buscando || q.trim().length < 3}
              className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
            >
              {buscando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Buscar
            </button>
          </div>

          {resultados !== null && resultados.length === 0 && (
            <p className="text-sm text-fg-faint">
              Sin coincidencias. Verifica el nombre o correo; si la persona no existe aún, usa
              “Agregar representante”.
            </p>
          )}

          {resultados !== null && resultados.length > 0 && (
            <ul className="space-y-2">
              {resultados.map((r) => {
                const deEstaEmpresa = r.registroLegalId === registroLegalId;
                const activo = seleccionado?.id === r.id;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      disabled={deEstaEmpresa}
                      onClick={() => setSeleccionado(activo ? null : r)}
                      className={`w-full rounded border p-2 text-left transition ${
                        deEstaEmpresa
                          ? "cursor-not-allowed opacity-60"
                          : activo
                            ? "cursor-pointer border-primary-strong bg-primary-tint"
                            : "cursor-pointer bg-surface-2 hover:border-primary-strong"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-fg">{r.nombreCompleto}</span>
                        {r.correo && <span className="text-xs text-fg-subtle">{r.correo}</span>}
                        {deEstaEmpresa && <StatusBadge tone="neutral">ya en esta empresa</StatusBadge>}
                        {r.usuarioId ? (
                          <StatusBadge tone="ok">con cuenta SICEN</StatusBadge>
                        ) : (
                          <StatusBadge tone="neutral">sin cuenta</StatusBadge>
                        )}
                        {activo && <Check className="ml-auto h-4 w-4 text-primary-strong" />}
                      </div>
                      <div className="mt-0.5 text-xs text-fg-muted">
                        Representante de: {r.registroRazonSocial ?? "—"}
                        {r.registroRfc ? ` (${r.registroRfc})` : ""}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {seleccionado && (
            <label className="flex items-center gap-2 text-xs text-fg-muted">
              <input
                type="checkbox"
                checked={notificar}
                onChange={(e) => setNotificar(e.target.checked)}
              />
              Enviar correo de asociación a {seleccionado.correo ?? "—"}
            </label>
          )}

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-surface-2 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => mut.mutate()}
            disabled={!seleccionado || mut.isPending}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            {mut.isPending ? "Asociando…" : "Asociar a esta razón social"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Captura de la autorización de un usuario para operar trámites (F1,
 * requerimiento RL 2026-07-15). La cuenta SICEN ya existe (la resolvió
 * UserSearchOrCreate); aquí se define el alcance: facultad, sectores
 * (obligatorio ≥1, default deny), puesto, otorgante y vigencia. El documento
 * de respaldo es opcional y se vincula después a la carta interna.
 */
function AltaUsuarioAutorizadoModal({
  registroLegalId,
  razonSocial,
  usuarioId,
  reclasificarRepresentante,
  notificar,
  representantes,
  onClose,
  onSaved,
}: {
  registroLegalId: string;
  razonSocial: string;
  /** Alta normal: cuenta ya resuelta por UserSearchOrCreate. */
  usuarioId?: string;
  /** Modo reclasificación (F2): el backend toma la cuenta del representante. */
  reclasificarRepresentante?: { id: string; nombre: string };
  notificar: boolean;
  representantes: RepResumen[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [tipo, setTipo] = useState("GESTION");
  const [sectoresSel, setSectoresSel] = useState<string[]>([]);
  const [puesto, setPuesto] = useState("");
  const [otorgante, setOtorgante] = useState("");
  const [vigenciaFin, setVigenciaFin] = useState("");
  /**
   * Representante legal y usuario autorizado no son papeles excluyentes
   * (requerimiento 2026-08-15 §3/§4): dependen de la relación que se consulte.
   * Por eso lo predeterminado es CONSERVAR la representación y solo sumar la
   * autorización; la sustitución real queda como una decisión explícita.
   */
  const [conservarRepresentacion, setConservarRepresentacion] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () =>
      (await api.get<Sector[]>(`/api/catalogos/sectores`)).data,
  });

  const mut = useMutation({
    mutationFn: async () => {
      const alcance = {
        tipoAutorizacion: tipo,
        sectoresIds: sectoresSel,
        puestoArea: puesto.trim() || null,
        representanteLegalOtorganteId: otorgante || null,
        vigenciaFin: vigenciaFin ? new Date(vigenciaFin + "T23:59:59").toISOString() : null,
        notificar,
      };
      if (reclasificarRepresentante) {
        await api.post(
          `/api/representantes-legales/${reclasificarRepresentante.id}/reclasificar-a-usuario-autorizado`,
          { ...alcance, conservarRepresentacion },
        );
      } else {
        await api.post(`/api/registros-legales/${registroLegalId}/usuarios-autorizados`, {
          usuarioId,
          ...alcance,
        });
      }
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (sectoresSel.length === 0) {
      setErr("Selecciona al menos un sector (la autorización es por sector).");
      return;
    }
    // Un usuario autorizado opera con la autorización de un representante legal
    // vigente: sin otorgante la operación no puede sustentarse (el backend la rechaza).
    if (!otorgante) {
      setErr("Selecciona el representante legal que otorga la autorización.");
      return;
    }
    setErr(null);
    mut.mutate();
  };

  const sinOtorgantePosible = representantes.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            {reclasificarRepresentante
              ? `Autorizar a ${reclasificarRepresentante.nombre} para trámites`
              : `Autorización para trámites — ${razonSocial}`}
          </h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-4">
          <p className="rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
            {reclasificarRepresentante ? (
              <>
                La autorización que definas vale para trámites de <strong>{razonSocial}</strong>. Para
                que esta persona opere en nombre de otra empresa titular, autorízala en la carta poder
                correspondiente (pestaña «Empresas atendidas»): eso es otra relación y no depende de
                esta.
              </>
            ) : (
              <>
                El usuario podrá crear solicitudes, cargar documentos y dar seguimiento en los sectores
                que marques. No aparecerá como representante legal ni podrá administrar el Registro Legal.
              </>
            )}
          </p>

          {reclasificarRepresentante && (
            <div className="space-y-2 rounded-lg border p-3">
              <span className="block text-xs font-medium text-fg-muted">¿Qué pasa con su representación legal?</span>
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  className="mt-1 h-4 w-4"
                  checked={conservarRepresentacion}
                  onChange={() => setConservarRepresentacion(true)}
                />
                <span>
                  <strong>La conserva.</strong> Sigue siendo representante legal de {razonSocial} y
                  además queda autorizado para operar trámites. Los dos papeles conviven.
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  className="mt-1 h-4 w-4"
                  checked={!conservarRepresentacion}
                  onChange={() => setConservarRepresentacion(false)}
                />
                <span>
                  <strong>La pierde.</strong> Deja de representar a {razonSocial}: el representante
                  queda <strong>Sustituido</strong> (con su historial) y solo opera trámites. Elige
                  esto únicamente si la persona ya no es representante legal.
                </span>
              </label>
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de autorización</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            >
              <option value="GESTION">Gestión (crear y operar solicitudes)</option>
              <option value="CONSULTA">Consulta (solo lectura)</option>
              <option value="SEGUIMIENTO">Seguimiento</option>
              <option value="FIRMA">Firma</option>
              <option value="INTERNO_TITULAR">Interno del titular</option>
            </select>
          </label>

          <div>
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Sectores autorizados <span className="text-estado-err">*</span>
            </span>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(sectoresQ.data ?? []).map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={sectoresSel.includes(s.id)}
                    onChange={() =>
                      setSectoresSel(
                        sectoresSel.includes(s.id)
                          ? sectoresSel.filter((x) => x !== s.id)
                          : [...sectoresSel, s.id],
                      )
                    }
                  />
                  {s.nombre}
                </label>
              ))}
            </div>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Puesto o área en la empresa</span>
            <input
              value={puesto}
              onChange={(e) => setPuesto(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              placeholder="p. ej. Coordinador de certificaciones"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Representante legal que otorga la autorización <span className="text-estado-err">*</span>
            </span>
            <select
              value={otorgante}
              onChange={(e) => setOtorgante(e.target.value)}
              disabled={sinOtorgantePosible}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
            >
              <option value="">— Selecciona un representante legal vigente —</option>
              {representantes.map((r) => (
                <option key={r.id} value={r.id}>{r.nombreCompleto}</option>
              ))}
            </select>
            {sinOtorgantePosible && (
              <span className="mt-1 block text-xs text-estado-warn">
                {reclasificarRepresentante
                  ? "No hay otro representante legal vigente que pueda otorgar la autorización. Registra o reactiva otro representante antes de reclasificar a esta persona."
                  : "La empresa no tiene un representante legal vigente que pueda otorgar la autorización. Regístralo o reactívalo en la pestaña «Representantes legales»."}
              </span>
            )}
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Vigencia de la autorización (fin)</span>
            <input
              type="date"
              value={vigenciaFin}
              onChange={(e) => setVigenciaFin(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            />
            <span className="mt-1 block text-xs text-fg-subtle">Vacío = sin vencimiento.</span>
          </label>

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending || sinOtorgantePosible}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mut.isPending
                ? "Guardando…"
                : reclasificarRepresentante
                  ? conservarRepresentacion
                    ? "Autorizar y conservar representación"
                    : "Sustituir y autorizar"
                  : "Autorizar usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Edición del alcance de una autorización existente (facultad, sectores, puesto, otorgante, vigencia). */
function EditarUsuarioAutorizadoModal({
  item,
  representantes,
  onClose,
  onSaved,
}: {
  item: UsuarioAutorizadoItem;
  representantes: RepResumen[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [tipo, setTipo] = useState(item.tipoAutorizacion);
  const [sectoresSel, setSectoresSel] = useState<string[] | null>(null); // null = sin cambios
  const [puesto, setPuesto] = useState(item.puestoArea ?? "");
  const [otorgante, setOtorgante] = useState(item.representanteLegalOtorganteId ?? "");
  const [vigenciaFin, setVigenciaFin] = useState(
    item.vigenciaFin ? item.vigenciaFin.slice(0, 10) : "",
  );
  const [motivo, setMotivo] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["catalogo-sectores"],
    queryFn: async () =>
      (await api.get<Sector[]>(`/api/catalogos/sectores`)).data,
  });
  // Preselección: códigos actuales → ids del catálogo (una vez cargado).
  const sectoresActualesIds = (sectoresQ.data ?? [])
    .filter((s) => item.sectoresCodigos.includes(s.codigo))
    .map((s) => s.id);
  const seleccion = sectoresSel ?? sectoresActualesIds;

  const mut = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/usuarios-autorizados/${item.asociacionId}`, {
        motivo: motivo.trim(),
        tipoAutorizacion: tipo !== item.tipoAutorizacion ? tipo : null,
        sectoresIds: sectoresSel,
        puestoArea: puesto,
        actualizarOtorgante: true,
        representanteLegalOtorganteId: otorgante || null,
        actualizarVigenciaFin: true,
        vigenciaFin: vigenciaFin ? new Date(vigenciaFin + "T23:59:59").toISOString() : null,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!motivo.trim()) {
      setErr("El motivo del cambio es obligatorio (auditable).");
      return;
    }
    if (seleccion.length === 0) {
      setErr("La autorización requiere al menos un sector; para retirar el acceso usa Estatus.");
      return;
    }
    if (!otorgante) {
      setErr("Selecciona el representante legal que otorga la autorización.");
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Editar autorización — {item.nombreCompleto || item.correo}
          </h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-4">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de autorización</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            >
              <option value="GESTION">Gestión (crear y operar solicitudes)</option>
              <option value="CONSULTA">Consulta (solo lectura)</option>
              <option value="SEGUIMIENTO">Seguimiento</option>
              <option value="FIRMA">Firma</option>
              <option value="INTERNO_TITULAR">Interno del titular</option>
            </select>
          </label>

          <div>
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Sectores autorizados <span className="text-estado-err">*</span>
            </span>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(sectoresQ.data ?? []).map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={seleccion.includes(s.id)}
                    onChange={() =>
                      setSectoresSel(
                        seleccion.includes(s.id)
                          ? seleccion.filter((x) => x !== s.id)
                          : [...seleccion, s.id],
                      )
                    }
                  />
                  {s.nombre}
                </label>
              ))}
            </div>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Puesto o área en la empresa</span>
            <input
              value={puesto}
              onChange={(e) => setPuesto(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Representante legal que otorga la autorización <span className="text-estado-err">*</span>
            </span>
            <select
              value={otorgante}
              onChange={(e) => setOtorgante(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">— Selecciona un representante legal vigente —</option>
              {representantes.map((r) => (
                <option key={r.id} value={r.id}>{r.nombreCompleto}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Vigencia de la autorización (fin)</span>
            <input
              type="date"
              value={vigenciaFin}
              onChange={(e) => setVigenciaFin(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            />
            <span className="mt-1 block text-xs text-fg-subtle">Vacío = sin vencimiento.</span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">
              Motivo del cambio <span className="text-estado-err">*</span>
            </span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              placeholder="Queda registrado en bitácora"
            />
          </label>

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Reclasificación usuario autorizado → representante legal (F2, D-03). */
function ReclasificarARepresentanteModal({
  item,
  onClose,
  onSaved,
}: {
  item: UsuarioAutorizadoItem;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [tipoRepresentacion, setTipoRepresentacion] = useState("REPRESENTANTE_LEGAL");
  const [instrumentoNumero, setInstrumentoNumero] = useState("");
  const [notificar, setNotificar] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/api/usuarios-autorizados/${item.asociacionId}/reclasificar-a-representante`, {
        tipoRepresentacion,
        instrumentoNumero: instrumentoNumero.trim() || null,
        notificar,
      });
    },
    onSuccess: async () => {
      await onSaved();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">
            Reclasificar a {item.nombreCompleto || item.correo} como representante legal
          </h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-4">
          <p className="rounded-lg border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
            La autorización para trámites quedará <strong>Sustituida</strong> (con su historial) y la
            persona pasará a ser representante legal de la empresa, con su misma cuenta SICEN. El
            instrumento de poder se carga después en la sección de documentos del representante.
          </p>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Tipo de representación</span>
            <select
              value={tipoRepresentacion}
              onChange={(e) => setTipoRepresentacion(e.target.value)}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
            >
              {Object.entries(TIPO_REPRESENTACION_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-fg-muted">Número de instrumento (escritura o poder)</span>
            <input
              value={instrumentoNumero}
              onChange={(e) => setInstrumentoNumero(e.target.value)}
              maxLength={120}
              className="w-full rounded border bg-surface px-2 py-1.5 text-sm"
              placeholder="Opcional; puede capturarse después"
            />
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={notificar}
              onChange={(e) => setNotificar(e.target.checked)}
            />
            Notificar el cambio por correo
          </label>

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Reclasificar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
